import { AviaryProvider } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate } from "@shared/react-router-dom/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { ConnectCheckEmailCard } from "@unauthenticated/signupSignin/components/ConnectCheckEmailCard/ConnectCheckEmailCard";
import { ConnectNextStepsCard } from "@unauthenticated/signupSignin/components/ConnectNextStepsCard/ConnectNextStepsCard";
import { ConnectPageLayout } from "@unauthenticated/signupSignin/components/ConnectPageLayout/ConnectPageLayout";
import { ConnectPractitionerCard } from "@unauthenticated/signupSignin/components/ConnectPractitionerCard/ConnectPractitionerCard";
import { PatientConnectSignUpForm } from "@unauthenticated/signupSignin/components/PatientConnectSignUpForm/PatientConnectSignUpForm";

import { useConnectPractitionerProfileUnauthenticatedSignUpSignInQuery } from "./ConnectPractitionerProfile.query.generated";
import { formatVisitPrice } from "./visitPricing";

interface Props {
  patientAttributes: PatientAttributes;
  storeId?: string;
}

const PatientConnectSignUpPage = ({ patientAttributes, storeId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pracName, pracAvatar, pracFirstName, pracLastName, pracSlug, pracId, storeSlug } =
    patientAttributes;
  const [magicLinkEmail, setMagicLinkEmail] = useState<string | null>(null);

  const { data } = useConnectPractitionerProfileUnauthenticatedSignUpSignInQuery({
    variables: { slug: pracSlug ?? "" },
    skip: !pracSlug,
  });
  const providerProfile = data?.viewer?.connectPractitioner?.providerProfile;

  const firstVisit = formatVisitPrice(t, {
    priceRange: providerProfile?.firstVisitPriceRange,
    durationMinutes: providerProfile?.firstVisitDurationMinutes,
  });
  const subsequentVisit = formatVisitPrice(t, {
    priceRange: providerProfile?.subsequentVisitPriceRange,
    durationMinutes: providerProfile?.subsequentVisitDurationMinutes,
  });

  const signInHref = authRoutes.welcomeStart(storeSlug, "");

  const sidebar = (
    <>
      <ConnectPractitionerCard
        pracName={pracName}
        pracAvatar={pracAvatar}
        pracFirstName={pracFirstName}
        pracLastName={pracLastName}
        practitionerType={providerProfile?.practitionerType?.description}
        location={providerProfile?.cityState ?? undefined}
        firstVisitPrice={firstVisit?.label}
        isFirstVisitFree={firstVisit?.isFree}
        subsequentVisitPrice={subsequentVisit?.label}
        isSubsequentVisitFree={subsequentVisit?.isFree}
      />
      <ConnectNextStepsCard pracFirstName={pracFirstName ?? pracName} />
    </>
  );

  return (
    <AviaryProvider controlledTheme="light" noNormalization>
      <ConnectPageLayout signInHref={signInHref} onBackClick={() => navigate(-1)} sidebar={sidebar}>
        {magicLinkEmail === null ? (
          <PatientConnectSignUpForm
            signInHref={signInHref}
            storeId={storeId}
            requestedPractitionerId={pracId}
            onMagicLinkSent={setMagicLinkEmail}
          />
        ) : (
          <ConnectCheckEmailCard email={magicLinkEmail} />
        )}
      </ConnectPageLayout>
    </AviaryProvider>
  );
};

export { PatientConnectSignUpPage };
export type { Props as PatientConnectSignUpPageProps };
